<template>
    <common-content-spacer height="medium" class="clients-list">
        <common-buttons v-if="!isTabletSmall" align="right">
            <common-button
                type="link"
                size="tiny"
                :label="`+ ${$t('pages.company.company_clients.add_new_client')} (${initialTotalCount})`"
                @click="toggleAddClientModal()"
            ></common-button>
        </common-buttons>
        <div class="clients-list--filter">
            <text-input
                :placeholder="$t('forms.offer.offer_filter.fields.term.placeholder')"
                type="text"
                valuePath="filter"
                @change="searchChange"
            ></text-input>
            <div class="clients-list--filter--sort">
                <span @click="togleSortDropdown">
                    <div class="offer-filter__sort--btn offer-filter__sort--btn-sort">
                        <common-icon name="sort" size="large"></common-icon>
                        {{ defaultSortName }}
                    </div>
                    <common-dropdown
                        v-click-away="closeSortDropdown"
                        class="offer-filter__sort--dropdown"
                        :isOpen="isSortDropdownOpen"
                        :recalculatePosition="true"
                        :hasArrow="false"
                        arrowPositionX="0"
                        arrowPositionY="0"
                    >
                        <nav class="dropdown__menu">
                            <button class="form-input-select__options--none" @click="clearOrderBy({ path: 'orderBy' })">
                                {{ $t('common.microcopy.none') }}
                            </button>
                            <button v-for="sortOpt in sortOptions" :key="sortOpt" @click="sortClick(sortOpt)">
                                {{ $t(`${sortOpt.label}`) }}
                            </button>
                        </nav>
                    </common-dropdown>
                </span>
                <common-buttons v-if="isTabletSmall" align="right">
                    <common-button
                        type="link"
                        size="tiny"
                        :label="`+ ${$t('pages.company.company_clients.add_new_client')} (${initialTotalCount})`"
                        @click="toggleAddClientModal()"
                    ></common-button>
                </common-buttons>
            </div>
        </div>
        <div class="clients-table-header">
            <p v-html="$t('pages.company.company_clients.number_of_clients', { number: initialTotalCount })"></p>
        </div>
        <common-table>
            <thead>
                <tr>
                    <th>{{ $t('pages.company.company_clients.first_and_last_name') }}</th>
                    <th>{{ $t('pages.company.company_clients.email') }}</th>
                    <th>{{ $t('pages.company.company_clients.phone_number') }}</th>
                    <th>{{ $t('pages.company.company_clients.history') }}</th>
                    <th>{{ $t('pages.company.company_clients.records') }}</th>
                    <th>{{ $t('pages.company.company_clients.active') }}</th>
                    <th>{{ $t('pages.company.company_clients.statistics') }}</th>
                    <th>{{ $t('common.microcopy.edit') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in clientsList" :key="index">
                    <td :data-label="$t('pages.company.company_clients.first_and_last_name')" class="td-full-width">
                        <div class="clients-list--name">
                            <common-user-avatar
                                :shouldLeadToProfile="true"
                                :withBorder="true"
                                :user="item"
                                size="medium-small"
                            ></common-user-avatar>
                            <p>{{ item.firstName }} {{ item.lastName }}</p>
                        </div>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.email')" class="td-full-width">
                        <p class="clients-list--contact clients-list--contact--email">
                            <a :href="`mailto:${item.email}`">{{ item.email }}</a>
                        </p>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.phone_number')" class="td-full-width">
                        <p class="clients-list--contact">
                            <a :href="`tel:${item.mobile}`">{{ item.mobile }}</a>
                        </p>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.history')">
                        <common-icon
                            class="clients-list--history"
                            name="reservation"
                            size="large"
                            @click="openClientHistoryModal(item)"
                        ></common-icon>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.records')">
                        <common-icon
                            class="clients-list--record"
                            name="records"
                            size="large"
                            @click="openClientRecordModal(item)"
                        ></common-icon>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.active')">
                        <common-icon
                            class="clients-list--block"
                            :class="{ 'clients-list--block--disabled': item.id === $auth.getUser().id }"
                            :name="item.companyClient?.blocked ? 'checkbox-off' : 'checkbox-on'"
                            size="large"
                            @click="blockUnblockClient(item)"
                        ></common-icon>
                    </td>
                    <td :data-label="$t('pages.company.company_clients.statistics')">
                        <common-icon
                            class="clients-list--statistics"
                            :name="'statistics'"
                            size="large"
                            @click="goToStatistics(item)"
                        ></common-icon>
                    </td>
                    <td v-if="item.finalizeAccountToken?.length" :data-label="$t('common.microcopy.edit')">
                        <common-icon
                            class="clients-list--block"
                            :class="{ 'clients-list--block--disabled': item.id === $auth.getUser().id }"
                            :name="'edit'"
                            size="medium"
                            @click="openEditClientModal(item)"
                        ></common-icon>
                    </td>
                </tr>
            </tbody>
        </common-table>
        <common-buttons v-show="clientsList.length < totalCount">
            <common-button
                type="primary"
                :isProcessing="isLoading"
                :label="$t('common.microcopy.show_more')"
                @click="loadMore()"
            ></common-button>
        </common-buttons>
        <add-client-modal
            v-if="isAddClientModalOpen"
            :prefillClient="prefillClient"
            :company="company"
            @close="closeAddClientModal"
            @client-added="clientAdded"
        ></add-client-modal>

        <client-record-modal
            v-if="isClientRecordModalOpen"
            :clientId="clientRecord?.id"
            :companyId="$route.params.id"
            :clientRecordsProp="clientRecord"
            @close="closeClientRecordModal"
        ></client-record-modal>
        <client-history-modal
            v-if="isClientHistoryModalOpen"
            :clientHistoryProp="clientHistory"
            @close="closeClientHistoryModal"
        ></client-history-modal>
    </common-content-spacer>
</template>

<script>
    import CompanyAPI from 'api/CompanyAPI'
    import debounce from 'lodash.debounce'
    import TextInput from 'components/form-inputs/TextInput'
    import ClientsSortFilterEnum from 'enums/clients_sort_filter.enum'
    import AddClientModal from 'components/modals/company/AddClientModal'
    import ClientRecordModal from 'components/modals/company/ClientRecordModal'
    import ClientHistoryModal from 'components/modals/company/ClientHistoryModal'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import Store from '~/store'
    import RoutesEnum from 'enums/routes.enum'

    export default {
        mixins: [WindowResizeMixin],

        components: { TextInput, AddClientModal, ClientRecordModal, ClientHistoryModal },

        props: {
            company: { type: Object, default: undefined },
        },

        data() {
            return {
                clientsList: [],
                limit: 10,
                offset: 0,
                totalCount: null,
                isLoading: false,
                filterApplied: {},
                isSortDropdownOpen: false,
                MIN_LENGTH: 3,
                sortLabel: 'sort',
                orderBy: {},
                isAddClientModalOpen: false,
                isClientRecordModalOpen: false,
                clientRecord: {},
                isClientHistoryModalOpen: false,
                clientHistory: {},
                initialTotalCount: 0,
                prefillClient: null,
            }
        },

        computed: {
            sortOptions() {
                return Object.values(ClientsSortFilterEnum).map((status) => ({
                    label: this.$t(
                        `forms.reservations.reservation_filter.sort.${status.value.orderBy}_${status.value.wayDefinition}`,
                    ),
                    status,
                    value: status.value,
                }))
            },

            defaultSortName() {
                return this.$t(`forms.reservations.reservation_filter.sort.${this.sortLabel}`)
            },
        },

        methods: {
            loadMore() {
                this.offset += this.limit
                this.getClientList(true)
            },

            clientAdded() {
                this.closeAddClientModal()
                this.initialTotalCount += 1
                this.getClientList()
            },

            searchChange: debounce(function ({ path, value }) {
                this[path] = value
                if (!value && !path) {
                    return
                }
                if (!value && path) {
                    this.getClientList()
                    return
                }
                if (value?.length < this.MIN_LENGTH) {
                    return
                }
                this.limit = 10
                this.offset = 0
                this.getClientList()
            }, 500),

            clearOrderBy() {
                this.sortLabel = 'sort'
                this.orderBy = {}
                this.getClientList()
            },

            sortClick(sortOpt) {
                this.sortLabel = `${sortOpt.value.orderBy}_${sortOpt.value.wayDefinition}`
                this.orderBy = {}
                this.orderBy.orderBy = sortOpt.value.orderBy
                this.orderBy.wayDefinition = sortOpt.value.wayDefinition
                this.getClientList()
            },

            togleSortDropdown() {
                this.isSortDropdownOpen = !this.isSortDropdownOpen
                this.showFilter = false
            },

            closeSortDropdown() {
                this.isSortDropdownOpen = false
            },

            getClientList(append) {
                if (!this.initialTotalCount) {
                    Store.dispatch('configuration/setIsLoading', true)
                }
                this.isLoading = true
                const companyId = this.$route.params.id
                const request = {
                    filter: this.filter?.length > 2 ? this.filter : undefined,
                    take: this.limit,
                    skip: this.offset,
                    wayDefinition: this.orderBy.wayDefinition,
                    oderBy: this.orderBy.orderBy,
                }
                CompanyAPI.getCompanyClients(companyId, request)
                    .then((response) => {
                        this.totalCount = response.total
                        if (!this.initialTotalCount) {
                            this.initialTotalCount = response.total
                        }
                        if (append) {
                            this.clientsList = [...this.clientsList, ...response.page]
                        } else {
                            this.clientsList = response.page
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                        Store.dispatch('configuration/setIsLoading', false)
                    })
            },

            blockUnblockClient(item) {
                const companyId = this.$route.params.id
                CompanyAPI.blockUnblock(item.id, companyId).then(() => {
                    if (item.companyClient?.blocked) {
                        item.companyClient.blocked = !item.companyClient?.blocked
                    } else {
                        item.companyClient = {
                            blocked: true,
                        }
                    }
                    this.$toast.success(this.$t('common.microcopy.success'))
                })
            },

            goToStatistics(item) {
                const routeData = this.$router.resolve({
                    name: RoutesEnum.COMPANY.MY_COMPANY,
                    params: { id: this.companyId, tabType: 'chart' },
                    query: { client: JSON.stringify(item) },
                })
                window.open(routeData.href, '_blank')
            },

            openClientHistoryModal(item) {
                this.clientHistory = item
                this.isClientHistoryModalOpen = true
            },

            closeClientHistoryModal() {
                this.isClientHistoryModalOpen = false
            },

            openClientRecordModal(item) {
                this.clientRecord = item
                this.isClientRecordModalOpen = true
            },

            closeClientRecordModal() {
                this.isClientRecordModalOpen = false
            },

            toggleAddClientModal() {
                this.isAddClientModalOpen = !this.isAddClientModalOpen
            },

            openEditClientModal(item) {
                this.prefillClient = item
                this.isAddClientModalOpen = true
            },

            closeAddClientModal() {
                this.prefillClient = null
                this.isAddClientModalOpen = false
            },
        },

        mounted() {
            this.getClientList()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .clients-table-header {
        display: flex;
        justify-content: space-between;
    }

    .clients-list {
        &--filter {
            display: flex;
            justify-content: space-between;
            align-items: center;

            &--sort {
                @include breakpoint(tablet-small) {
                    width: 100%;
                    flex-direction: row-reverse;
                }
                margin-top: 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            @include breakpoint(tablet-small) {
                flex-wrap: wrap;
            }

            .form-input-text {
                @include breakpoint(tablet-small) {
                    min-width: 100%;
                }
                min-width: 400px;
            }
        }

        &--block {
            cursor: pointer;
            color: $color--accent-1;

            &--disabled {
                opacity: 0.5;
                pointer-events: none;
            }
        }

        &--name {
            display: flex;
            align-items: center;
            justify-content: flex-start;

            @include breakpoint(tablet-small) {
                justify-content: flex-end;
            }

            @include breakpoint(smartphone-large) {
                justify-content: flex-start;
            }

            .avatar {
                margin-right: 10px;
            }
        }

        &--record {
            cursor: pointer;
            color: $color--accent-9;
        }

        &--statistics {
            cursor: pointer;
            color: $color-blue--facebook;
        }

        &--history {
            cursor: pointer;
            color: $color-blue--facebook;
        }

        &--contact {
            color: $color--accent-1;

            &--email {
                @include breakpoint(tablet-small) {
                    max-width: 400px;
                }
                @include breakpoint(smartphone-large) {
                    max-width: 300px;
                }
                @include ellipsis();

                a {
                    word-break: break-word;
                    width: 100%;
                    display: block;
                    max-width: 100%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        }
    }
</style>
